import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { forumApi } from '../api/forum';
import { UserContext } from '../context/UserContext';
import { isModerator } from '../admin/utils/roles';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import { SkeletonCard } from '../components/controls/HrnbSkeleton';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import { PlusIcon, ClearIcon, CreateProfileIcon } from '../icons';
import '../css/pages/ForumPage.css';
import HrnbSearchFilter from "../components/controls/HrnbSearchFilter";
import HrnbSelect from "../components/controls/HrnbSelect";
import HrnbErrorBoundary from "../components/controls/HrnbErrorBoundary";
import HrnbPagination from "../components/controls/HrnbPagination";
import PostCard from "../components/forum/PostCard";
import CreatePostForm from "../components/forum/CreatePostForm";
import TagList from "../components/forum/TagList";
import PopularPosts from "../components/forum/PopularPosts";
import RecentActivePosts from "../components/forum/RecentActivePosts";

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
    { value: 'createdAt', label: 'По дате создания' },
    { value: 'updatedAt', label: 'По дате обновления' },
    { value: 'likes', label: 'По лайкам' },
    { value: 'commentCount', label: 'По комментариям' },
];

export default function ForumPage() {
    const { currentUser } = useContext(UserContext);

    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [categoryFilter, setCategoryFilter] = useState('all');
    const [tagFilter, setTagFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [groupedByCategory, setGroupedByCategory] = useState(categoryFilter === 'all');
    const [showScrollTop, setShowScrollTop] = useState(false);

    const abortControllerRef = useRef(null);
    const fetchTimeoutRef = useRef(null);

    const fetchPosts = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage - 1,
                size: PAGE_SIZE,
                sortBy,
                direction: 'DESC',
            };

            // Если группировка по категориям включена, не фильтруем по categoryId
            if (categoryFilter && categoryFilter !== 'all' && !groupedByCategory) {
                params.categoryId = categoryFilter;
            }
            if (tagFilter) {
                params.tag = tagFilter;
            }
            if (statusFilter) {
                params.status = statusFilter;
            }

            const data = await forumApi.listPosts(params, abortControllerRef.current.signal);
            setPosts(data.content || data.posts || data || []);
            setTotal(data.totalElements || data.total || 0);
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[ForumPage] Failed to fetch posts:', err.message);
            setError('Не удалось загрузить посты форума');
        } finally {
            setLoading(false);
        }
    }, [currentPage, sortBy, categoryFilter, tagFilter, statusFilter, groupedByCategory]);

    const fetchCategories = useCallback(async () => {
        try {
            const data = await forumApi.listCategories();
            setCategories(data || []);
        } catch (err) {
            console.error('[ForumPage] Failed to fetch categories:', err.message);
            setCategories([
                { id: 1, name: 'Обсуждения' },
                { id: 2, name: 'Вопросы и ответы' },
                { id: 3, name: 'Новости' },
                { id: 4, name: 'Предложения' },
            ]);
        }
    }, []);

useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    // Отслеживание скролла для кнопки «Наверх»
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 400);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const performSearch = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchPerformed(false);
            setCurrentPage(1);
            fetchPosts();
            return;
        }
        try {
            setLoading(true);
            setError(null);
            setSearchPerformed(true);
            const data = await forumApi.search({ q: query.trim(), page: 0, size: PAGE_SIZE });
            setPosts(data.content || data.results || data || []);
            setTotal(data.totalElements || data.total || 0);
            setCurrentPage(1);
        } catch (err) {
            console.error('[ForumPage] Search failed:', err.message);
            setError('Не удалось выполнить поиск');
        } finally {
            setLoading(false);
        }
    }, [fetchPosts]);

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!query) {
            setSearchPerformed(false);
            fetchPosts();
        }
    };

    const handleSearchSubmit = (query) => {
        performSearch(query);
    };

    const handleClear = () => {
        setSearchQuery('');
        setSearchPerformed(false);
        setCategoryFilter('');
        setTagFilter('');
        setSortBy('createdAt');
        setStatusFilter('');
        setCurrentPage(1);
        fetchPosts();
    };

    const handleTagSelect = (tag) => {
        const tagSlug = tag.slug || tag.name?.toLowerCase().replace(/\s+/g, '-');
        if (tagFilter === tagSlug) {
            setTagFilter('');
        } else {
            setTagFilter(tagSlug);
            setCurrentPage(1);
            setSearchQuery('');
            setSearchPerformed(false);
        }
    };

    const handleCreateSuccess = () => {
        setIsCreateModalOpen(false);
        setCurrentPage(1);
        fetchPosts();
    };

    const handlePinToggle = () => {
        fetchPosts();
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleCategoryChange = (value) => {
        setCategoryFilter(value);
        setGroupedByCategory(value === 'all');
        setSearchPerformed(false);
        if (currentPage !== 1) setCurrentPage(1);
    };

    const handleSortChange = (value) => {
        setSortBy(value);
        setSearchPerformed(false);
        if (currentPage !== 1) setCurrentPage(1);
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
        setSearchPerformed(false);
        if (currentPage !== 1) setCurrentPage(1);
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // Разделяем посты текущей страницы на закреплённые и обычные (клиент-side)
    // Сервер не поддерживает фильтр по PINNED, поэтому посты приходят вперемешку
    const pinnedPosts = posts.filter(p => p.status === 'PINNED');
    const regularPosts = posts.filter(p => p.status !== 'PINNED');

    return (
        <div className="forum-page">
            <div className="forum-page-header">
                <h1 className="forum-page-title">Форум</h1>
                {currentUser && (
                    <HrnbButton variant="primary" onClick={() => setIsCreateModalOpen(true)} icon={<PlusIcon />}>
                        Создать пост
                    </HrnbButton>
                )}
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="forum-page-search">
                <HrnbSearchFilter
                    search={searchQuery}
                    onSearchChange={handleSearch}
                    onSearchSubmit={handleSearchSubmit}
                    onClear={handleClear}
                    placeholder="Поиск по форуму..."
                    debounceMs={300}
                />
            </div>

            <div className="forum-page-filters">
                <div className="filter-group">
                    <label className="filter-label">Сортировка:</label>
                    <HrnbSelect
                        value={sortBy}
                        onChange={handleSortChange}
                        options={SORT_OPTIONS}
                        className="filter-select"
                    />
                </div>
                {isModerator(currentUser) && (
                    <div className="filter-group">
                        <label className="filter-label">Статус:</label>
                        <HrnbSelect
                            value={statusFilter}
                            onChange={handleStatusChange}
                            options={[
                                { value: '', label: 'Все статусы' },
                                { value: 'DRAFT', label: 'Черновик' },
                                { value: 'PUBLISHED', label: 'Опубликован' },
                                { value: 'ARCHIVED', label: 'В архиве' },
                                { value: 'MODERATED', label: 'На модерации' },
                            ]}
                            className="filter-select"
                        />
                    </div>
                )}
            </div>

            {/* Вкладки категорий */}
            {categories.length > 0 && (
                <div className="forum-categories-tabs">
                    <HrnbTabs
                        value={categoryFilter}
                        onChange={(value) => {
                            setCategoryFilter(value);
                            setGroupedByCategory(value === 'all');
                            setCurrentPage(1);
                        }}
                    >
                        <HrnbTab label="Все категории" value="all" />
                        {categories.map((cat) => (
                            <HrnbTab key={cat.id} label={cat.name} value={String(cat.id)} />
                        ))}
                    </HrnbTabs>
                </div>
            )}

            <div className="forum-page-content">
                <div className="forum-page-main">
                    <HrnbErrorBoundary title="Ошибка загрузки постов" message="Не удалось отобразить список постов.">
                    {loading ? (
                        <div className="forum-posts-grid">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </div>
                    ) : posts.length > 0 ? (
                        <>
                            {/* Индикатор активного поиска */}
                            {searchPerformed && searchQuery.trim() && (
                                <div className="forum-search-active">
                                    <span>Результаты поиска по запросу: <strong>«{searchQuery.trim()}»</strong></span>
                                    <span className="forum-search-count">Найдено: {total}</span>
                                </div>
                            )}
                            {/* Закреплённые посты вверху списка */}
                            {pinnedPosts.length > 0 && (
                                <div className="forum-pinned-section">
                                    <div className="forum-pinned-section-header">
                                        <h2 className="forum-pinned-section-title">📌 Закреплённые посты</h2>
                                        <span className="forum-pinned-section-count">
                                            {pinnedPosts.length} {pinnedPosts.length === 1 ? 'пост' : pinnedPosts.length < 5 ? 'поста' : 'постов'}
                                        </span>
                                    </div>
                                    <div className="forum-posts-grid">
                                        {pinnedPosts.map((post) => (
                                            <PostCard key={post.id} post={post} onPinToggle={handlePinToggle} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Если "Все категории", группируем по категориям */}
                            {groupedByCategory && categories.length > 0 && (
                                <div className="forum-grouped-posts">
                                    {categories.map((category) => {
                                        const categoryPosts = regularPosts.filter(p => {
                                            // Получаем ID категории из поста разными способами
                                            const postId = p.category?.id || p.categoryId;
                                            const categoryName = typeof p.category === 'string' ? p.category : p.category?.name;

                                            // Сравниваем по ID или по имени
                                            return postId === category.id ||
                                                   postId === String(category.id) ||
                                                   categoryName === category.name;
                                        });

                                        if (categoryPosts.length === 0) return null;

                                        return (
                                            <div key={category.id} className="forum-category-group">
                                                <div className="forum-category-group-header">
                                                    <h2 className="forum-category-group-title">{category.name}</h2>
                                                    <span className="forum-category-group-count">
                                                        {categoryPosts.length} {categoryPosts.length === 1 ? 'пост' : categoryPosts.length < 5 ? 'поста' : 'постов'}
                                                    </span>
                                                </div>
                                                <div className="forum-posts-grid">
                                                    {categoryPosts.map((post) => (
                                                        <PostCard key={post.id} post={post} onPinToggle={handlePinToggle} />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Посты без категории */}
                                    {(() => {
                                        const postsWithoutCategory = regularPosts.filter(p =>
                                            !p.category?.id && !p.categoryId && !p.category
                                        );

                                        if (postsWithoutCategory.length === 0) return null;

                                        return (
                                            <div key="uncategorized" className="forum-category-group">
                                                <div className="forum-category-group-header">
                                                    <h2 className="forum-category-group-title">Без категории</h2>
                                                    <span className="forum-category-group-count">
                                                        {postsWithoutCategory.length} {postsWithoutCategory.length === 1 ? 'пост' : postsWithoutCategory.length < 5 ? 'поста' : 'постов'}
                                                    </span>
                                                </div>
                                                <div className="forum-posts-grid">
                                                    {postsWithoutCategory.map((post) => (
                                                        <PostCard key={post.id} post={post} onPinToggle={handlePinToggle} />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Если выбрана конкретная категория, показываем обычную сетку */}
                            {!groupedByCategory && categoryFilter !== 'all' && (
                                <div className="forum-posts-grid">
                                    {regularPosts.map((post) => (
                                        <PostCard key={post.id} post={post} onPinToggle={handlePinToggle} />
                                    ))}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <HrnbPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    ) : (
                        <div className="forum-page-empty">
                            {searchPerformed && searchQuery.trim() ? (
                                <p>По запросу «<strong>{searchQuery.trim()}</strong>» ничего не найдено. Попробуйте изменить запрос.</p>
                            ) : (
                                <p>Постов не найдено</p>
                            )}
                            {currentUser && !searchPerformed && (
                                <HrnbButton variant="primary" onClick={() => setIsCreateModalOpen(true)} icon={<CreateProfileIcon />}>
                                    Создать первый пост
                                </HrnbButton>
                            )}
                        </div>
                    )}
                    </HrnbErrorBoundary>
                </div>

                <aside className="forum-page-sidebar">
                    {tagFilter && (
                        <div className="forum-active-filter">
                            <span>Фильтр по тегу: <strong>#{tagFilter}</strong></span>
                            <HrnbButton variant="outline" size="sm" onClick={() => setTagFilter('')} icon={<ClearIcon />}>
                                Сбросить
                            </HrnbButton>
                        </div>
                    )}
                    <HrnbErrorBoundary title="Ошибка загрузки тегов" message="Не удалось загрузить список тегов.">
                        <TagList limit={15} onTagSelect={handleTagSelect} activeTag={tagFilter} />
                    </HrnbErrorBoundary>
                    <HrnbErrorBoundary title="Ошибка загрузки виджета" message="Не удалось загрузить популярные посты.">
                        <PopularPosts />
                    </HrnbErrorBoundary>
                    <HrnbErrorBoundary title="Ошибка загрузки виджета" message="Не удалось загрузить активные посты.">
                        <RecentActivePosts />
                    </HrnbErrorBoundary>
                </aside>
            </div>

            {/* Кнопка «Наверх» */}
            {showScrollTop && (
                <button
                    className="forum-scroll-top"
                    onClick={scrollToTop}
                    aria-label="Наверх"
                    title="Наверх"
                >
                    ↑
                </button>
            )}

            <CreatePostForm
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={handleCreateSuccess}
                categories={categories}
            />
        </div>
    );
}